
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { 
  getLicenseKeys, 
  getDomainRegistrations, 
  getSuperAdminLicenses, 
} from '@/utils/licenseService';
import { 
  LicenseKey,
  DomainRegistration,
  SuperAdminLicense,
} from '@/types/licenseKey';
import { isAuthenticated, logoutUser, getCurrentUser } from '@/utils/auth';

export const useBaseLicenseData = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [licenseKeys, setLicenseKeys] = useState<LicenseKey[]>([]);
  const [domains, setDomains] = useState<DomainRegistration[]>([]);
  const [superAdminLicenses, setSuperAdminLicenses] = useState<SuperAdminLicense[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('dashboard');

  // Check authentication and load data
  useEffect(() => {
    const checkAuth = async () => {
      if (!isAuthenticated()) {
        navigate('/login');
        return;
      }
      
      const user = getCurrentUser();
      // In a real app, we would check if the user is a Master License admin
      
      setIsLoading(true);
      try {
        const [keys, domainRegs, licenses] = await Promise.all([
          getLicenseKeys(),
          getDomainRegistrations(),
          getSuperAdminLicenses()
        ]);
        
        setLicenseKeys(keys);
        setDomains(domainRegs);
        setSuperAdminLicenses(licenses);
      } catch (error) {
        console.error('Error loading master license data:', error);
        toast({
          title: "Error",
          description: "Failed to load license data",
          variant: "destructive",
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuth();
  }, [navigate, toast]);

  // Handle logout
  const handleLogout = async () => {
    try {
      await logoutUser();
      navigate('/login');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Handle search filtering
  const filteredLicenseKeys = licenseKeys.filter(key => 
    key.key.toLowerCase().includes(searchQuery.toLowerCase()) ||
    key.domain.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (key.superAdminName && key.superAdminName.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  return {
    isLoading,
    licenseKeys,
    setLicenseKeys,
    domains,
    setDomains,
    superAdminLicenses,
    setSuperAdminLicenses,
    searchQuery,
    setSearchQuery,
    activeTab,
    setActiveTab,
    filteredLicenseKeys,
    handleLogout,
    toast,
    navigate
  };
};
